// One source of truth for service worker registration.
//
// Why this exists: this app needs the SW for two unrelated reasons —
//   (1) PWA install + offline asset caching (Workbox in the SW), and
//   (2) Firebase push messaging.
// Both want scope "/", so they have to be the same SW registration. If
// index.js registers one and usePushNotifications registers another, the
// second one evicts the first, which is exactly the bug we just hit on prod.
//
// The Firebase config goes in the URL query string so the SW can call
// firebase.initializeApp() without us hardcoding values inside the SW file.

import { firebaseConfig } from './FirebaseInit';

function swUrl() {
  const qs = new URLSearchParams({
    apiKey: firebaseConfig.apiKey || '',
    authDomain: firebaseConfig.authDomain || '',
    projectId: firebaseConfig.projectId || '',
    messagingSenderId: firebaseConfig.messagingSenderId || '',
    appId: firebaseConfig.appId || '',
  });
  return `/firebase-messaging-sw.js?${qs.toString()}`;
}

let registrationPromise = null;

/**
 * Remove stale CRA service-worker.js registrations from returning users'
 * browsers. Selective on purpose — calling CRA's blanket .unregister() races
 * with our own register() (whichever wins `navigator.serviceWorker.ready`
 * gets torn down) and on dev that means we kill the SW we just installed.
 */
async function purgeStaleCRAWorkers() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      regs.map(async (reg) => {
        const scriptURL =
          (reg.active && reg.active.scriptURL) ||
          (reg.waiting && reg.waiting.scriptURL) ||
          (reg.installing && reg.installing.scriptURL) ||
          '';
        // Match CRA's filename specifically — never unregister anything that
        // looks like our firebase-messaging-sw.
        if (/\/service-worker\.js(\?|$)/.test(scriptURL)) {
          await reg.unregister().catch(() => {});
        }
      })
    );
  } catch (err) {
    console.warn('[sw] purge failed', err);
  }
}

export function registerAppServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }
  if (!registrationPromise) {
    // Purge first, then register. If the new registration happens before the
    // old one is gone, the browser still replaces it at the same scope — but
    // doing it in this order avoids any window where two SWs are fighting
    // for control of the page.
    registrationPromise = purgeStaleCRAWorkers()
      .then(() => navigator.serviceWorker.register(swUrl()))
      .catch((err) => {
        console.warn('[sw] register failed', err);
        registrationPromise = null; // allow retry on next call
        return null;
      });
  }
  return registrationPromise;
}

export function getAppServiceWorkerRegistration() {
  // Wait for whatever's been registered to become active.
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }
  return navigator.serviceWorker.ready;
}
